import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { DirectionService } from '../../direction.service';
import { DialogService } from '../dialog.service';
import { OrdersService } from '../../main-section/printing-table/order.service';

export interface PaymentRequestDialogData {
  user: { _id?: string; email?: string };
}

@Component({
  selector: 'app-payment-request-dialog',
  templateUrl: './payment-request-dialog.component.html',
  styleUrls: ['./payment-request-dialog.component.css'],
  host: { class: 'fill-screen-modal-phone' },
})
export class PaymentRequestDialogComponent implements OnInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  amount: number | null = null;
  isSending = false;

  private directionSub?: Subscription;
  private darkModeSub?: Subscription;

  constructor(
    private directionService: DirectionService,
    private dialogService: DialogService,
    private ordersService: OrdersService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<PaymentRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentRequestDialogData
  ) {}

  get user(): PaymentRequestDialogData['user'] {
    return this.data?.user ?? {};
  }

  get userLabel(): string {
    return (this.user?.email && String(this.user.email).trim()) || '';
  }

  ngOnInit(): void {
    this.directionSub = this.directionService.direction$.subscribe((direction) => {
      this.isRTL = direction === 'rtl';
    });
    this.darkModeSub = this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
  }

  ngOnDestroy(): void {
    this.directionSub?.unsubscribe();
    this.darkModeSub?.unsubscribe();
  }

  closeDialog(): void {
    if (this.isSending) {
      return;
    }
    this.dialogService.onClosePaymentRequestDialog();
  }

  canSend(): boolean {
    return !this.isSending && this.amount != null && !isNaN(Number(this.amount)) && Number(this.amount) > 0;
  }

  onSend(): void {
    if (!this.canSend() || !this.user?._id) {
      return;
    }
    this.isSending = true;
    this.ordersService
      .createEmptyFixProductsOrder(this.user._id, Number(this.amount))
      .subscribe({
        next: (res) => {
          this.isSending = false;
          this.snackBar.open(
            this.translate.instant('su-management.users.payment-request-success'),
            '',
            { duration: 4000, verticalPosition: 'top', panelClass: ['auto-width', 'center-text'] }
          );
          this.dialogRef.close(res);
        },
        error: () => {
          this.isSending = false;
          this.snackBar.open(
            this.translate.instant('su-management.users.payment-request-failed'),
            '',
            { duration: 5000, verticalPosition: 'top', panelClass: ['auto-width', 'center-text'] }
          );
        },
      });
  }
}
